import type { Action, Agent, Observation } from "@cc/protocol";
import { formatTick } from "@cc/protocol";
import { DEFAULT_MATCH_SECONDS } from "@cc/engine";
import type { ChatMessage, ModelProvider } from "./provider.js";
import { buildCricketSystemPrompt, buildUserPrompt } from "./prompt.js";
import { parseAction } from "./parse.js";
import { BOT_PROFILES, createBotAgent } from "./heuristic.js";

export const FALLBACK_PREFIX = "[fallback]";

/**
 * Reasons that mean "a model was supposed to decide this turn and did not".
 *
 * Exported and centralised for two reasons. The web client detects a degraded
 * cricket by `FALLBACK_PREFIX`, so the spelling must be shared rather than
 * duplicated. And the i18n test enumerates every reachable reason by scanning
 * the agent sources for reason literals, so keeping these as plain literals is
 * what keeps them translated instead of silently falling through to English.
 */
export const FALLBACK_REASONS = {
  providerError: "[fallback] provider error",
  unparseable: "[fallback] unparseable response",
  degraded: "[fallback] degraded: provider unavailable",
  skipped: "[fallback] skipped: previous decision still in flight",
} as const;

export interface LlmAgentOptions {
  readonly id: string;
  readonly persona: string;
  readonly provider: ModelProvider;
  readonly model: string;
  readonly arenaSize?: number;
  readonly matchSeconds?: number;
  readonly visionRadius?: number;
  /** How many turns of its own decisions the agent is shown as history. */
  readonly memoryTurns?: number;
  /** Consecutive provider failures before falling back to the safety bot. */
  readonly failureThreshold?: number;
  readonly onDecision?: (record: DecisionRecord) => void;
}

export interface DecisionRecord {
  readonly tick: number;
  readonly agentId: string;
  readonly action: Action | null;
  readonly reason?: string;
  readonly latencyMs: number;
  readonly parseError?: string;
  readonly providerError?: string;
  readonly usedFallback: boolean;
  readonly raw?: string;
}

/**
 * An LLM-backed cricket.
 *
 * Three hard-won behaviours are baked in here, because all three are the
 * difference between a fun match and a broken one:
 *
 *  1. **Never stall the world.** A decision that is slow or invalid falls back
 *     to a heuristic on a per-turn basis. The simulation must never wait.
 *  2. **In-flight decisions are dropped, not queued.** If a model takes two
 *     seconds and the decision interval is one second, late answers are stale.
 *     Queueing them would make a slow model effectively invincible, which is
 *     both wrong and boring to watch.
 *  3. **Failures are recorded, not hidden.** Every malformed response is
 *     surfaced in the decision log and in the model's reliability stats.
 *
 * The three failure countermeasures above are per-turn. After
 * `failureThreshold` consecutive provider failures the agent enters degraded
 * mode and uses the heuristic directly until the provider recovers.
 */
export function createLlmAgent(options: LlmAgentOptions): Agent & {
  decisions: DecisionRecord[];
  stats(): LlmAgentStats;
  /** Identity of the fallback profile, exposed for the UI. */
  readonly fallbackPersona: string;
} {  const arenaSize = options.arenaSize ?? 100;
  const matchSeconds = options.matchSeconds ?? DEFAULT_MATCH_SECONDS;
  const memoryTurns = options.memoryTurns ?? 5;
  const failureThreshold = options.failureThreshold ?? 4;

  const systemPrompt = buildCricketSystemPrompt({
    persona: options.persona,
    model: options.model,
    arenaSize,
    matchSeconds,
    visionRadius: options.visionRadius,
  });

  const safetyBot = createBotAgent({
    ...BOT_PROFILES.collector,
    id: options.id,
    persona: options.persona,
  });

  /**
   * Wraps a fallback action so the timeline says a fallback happened.
   *
   * Without this, a provider failure is invisible. The safety bot returns an
   * action carrying the *bot's* reason ("moving to resource"), which is
   * indistinguishable from a model that genuinely decided the same thing, so a
   * dead provider plays out as a healthy model wearing the viewer's AI name.
   *
   * This matters more than it looks. The entertainment value of the arena rests
   * on the viewer being able to attribute play to a brain; misattributing a bot's
   * move to a model is not a cosmetic bug, it is the product lying. The web
   * client reads the `[fallback]` prefix to mark a cricket as degraded.
   */
  function markFallback(action: Action, reason: string): Action {
    return { ...action, reason };
  }

  const decisions: DecisionRecord[] = [];
  const memory: string[] = [];
  let consecutiveFailures = 0;
  let inFlight = 0;

  const telemetry = {
    turns: 0,
    parsed: 0,
    fallback: 0,
    latencyTotal: 0,
    latencyMax: 0,
    providerErrors: 0,
    degradedTurns: 0,
  };

  function record(entry: DecisionRecord): void {
    decisions.push(entry);
    options.onDecision?.(entry);
  }

  function noteMemory(obs: Observation, entry: DecisionRecord): void {
    const what = entry.action ? describeAction(entry.action) : "failed to act";
    const why = entry.reason ? ` (${entry.reason})` : "";
    const note = `${formatTick(obs.tick)} ${what}${why}`;
    memory.push(note);
    while (memory.length > memoryTurns * 3) memory.shift();
  }

  /** Record world-truth outcomes once so history shows failures, not only intents. */
  let lastNotedOutcomeTick = -1;
  function noteOutcome(obs: Observation): void {
    const o = obs.lastActionOutcome;
    if (!o || o.tick === lastNotedOutcomeTick) return;
    lastNotedOutcomeTick = o.tick;
    const tag = o.ok ? "OK" : "FAIL";
    memory.push(`${formatTick(o.tick)} RESULT ${tag}: ${o.detail}`);
    while (memory.length > memoryTurns * 3) memory.shift();
  }

  async function decide(obs: Observation): Promise<Action> {
    noteOutcome(obs);
    telemetry.turns++;
    const startedAt = Date.now();

    // Degraded mode: the provider has failed repeatedly, so stop paying for it.
    if (consecutiveFailures >= failureThreshold) {
      telemetry.degradedTurns++;
      const action = markFallback(
        await Promise.resolve(safetyBot.decide(obs)),
        FALLBACK_REASONS.degraded,
      );
      record({
        tick: obs.tick,
        agentId: options.id,
        action,
        reason: FALLBACK_REASONS.degraded,
        latencyMs: 0,
        usedFallback: true,
      });
      noteMemory(obs, { tick: obs.tick, agentId: options.id, action, latencyMs: 0, usedFallback: true });
      return action;
    }

    // Overlapping call: never let a slow model build a queue of stale moves.
    if (inFlight > 0) {
      const action = markFallback(
        await Promise.resolve(safetyBot.decide(obs)),
        FALLBACK_REASONS.skipped,
      );
      record({
        tick: obs.tick,
        agentId: options.id,
        action,
        reason: FALLBACK_REASONS.skipped,
        latencyMs: 0,
        usedFallback: true,
      });
      return action;
    }

    const messages: ChatMessage[] = [
      { role: "system", content: systemPrompt },
      { role: "user", content: buildUserPrompt(obs, memory) },
    ];

    inFlight++;
    let text: string | null = null;
    let providerError: string | undefined;
    try {
      text = await options.provider.complete(messages);
      consecutiveFailures = 0;
    } catch (err) {
      consecutiveFailures++;
      telemetry.providerErrors++;
      providerError = err instanceof Error ? err.message : String(err);
    } finally {
      inFlight--;
    }

    const latencyMs = Date.now() - startedAt;
    telemetry.latencyTotal += latencyMs;
    telemetry.latencyMax = Math.max(telemetry.latencyMax, latencyMs);

    if (text === null) {
      const action = markFallback(
        await Promise.resolve(safetyBot.decide(obs)),
        FALLBACK_REASONS.providerError,
      );
      telemetry.fallback++;
      record({
        tick: obs.tick,
        agentId: options.id,
        action,
        reason: FALLBACK_REASONS.providerError,
        latencyMs,
        usedFallback: true,
        providerError: providerError ?? "unknown",
      });
      noteMemory(obs, { tick: obs.tick, agentId: options.id, action, latencyMs, usedFallback: true });
      return action;
    }

    const outcome = parseAction(text, arenaSize);
    if (!outcome.ok || !outcome.action) {
      const action = markFallback(
        await Promise.resolve(safetyBot.decide(obs)),
        FALLBACK_REASONS.unparseable,
      );
      telemetry.fallback++;
      record({
        tick: obs.tick,
        agentId: options.id,
        action,
        reason: FALLBACK_REASONS.unparseable,
        latencyMs,
        usedFallback: true,
        parseError: outcome.error ?? "unknown",
        raw: text.slice(0, 500),
      });
      noteMemory(obs, { tick: obs.tick, agentId: options.id, action, latencyMs, usedFallback: true });
      return action;
    }

    telemetry.parsed++;
    const reason = "reason" in outcome.action ? outcome.action.reason : undefined;
    record({
      tick: obs.tick,
      agentId: options.id,
      action: outcome.action,
      latencyMs,
      usedFallback: false,
      ...(reason === undefined ? {} : { reason }),
      raw: text.slice(0, 500),
    });
    noteMemory(obs, {
      tick: obs.tick,
      agentId: options.id,
      action: outcome.action,
      latencyMs,
      usedFallback: false,
      ...(reason === undefined ? {} : { reason }),
    });
    return outcome.action;
  }

  return {
    id: options.id,
    persona: options.persona,
    model: options.model,
    fallbackPersona: BOT_PROFILES.collector.persona,
    decide,
    decisions,
    stats(): LlmAgentStats {
      return {
        ...telemetry,
        avgLatencyMs: telemetry.turns === 0 ? 0 : telemetry.latencyTotal / telemetry.turns,
        parseRate: telemetry.turns === 0 ? 1 : telemetry.parsed / telemetry.turns,
      };
    },
  };
}

export interface LlmAgentStats {
  turns: number;
  parsed: number;
  fallback: number;
  latencyTotal: number;
  latencyMax: number;
  providerErrors: number;
  degradedTurns: number;
  avgLatencyMs: number;
  parseRate: number;
}

function describeAction(action: Action): string {
  switch (action.action) {
    case "MOVE":
      return `MOVE -> [${Math.round(action.target[0])}, ${Math.round(action.target[1])}]`;
    case "SCOUT":
      return `SCOUT -> [${Math.round(action.target[0])}, ${Math.round(action.target[1])}]`;
    case "COLLECT":
      return `COLLECT ${action.resourceId ?? "nearest"}`;
    case "ATTACK":
      return `ATTACK ${action.targetId ?? "enemy"}`;
    case "DEFEND":
      return `DEFEND ${action.durationTicks ?? 10}t`;
    case "FLEE":
      return "FLEE";
    case "EXCHANGE":
      return `EXCHANGE ${action.item}`;
    case "USE":
      return `USE ${action.item}`;
    case "WAIT":
      return "WAIT";
  }
}
